
import { useParams, Navigate } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import NeighborhoodHero from './components/NeighborhoodHero';
import StickyNav from './components/StickyNav';
import OverviewStats from './components/OverviewStats';
import BusinessListings from './components/BusinessListings';
import NeighborhoodMap from './components/NeighborhoodMap';
import ContactCTA from './components/ContactCTA';
import MailchimpNewsletter from '../../../components/feature/MailchimpNewsletter';
import { getNeighborhoodBySlug } from '../../../mocks/neighborhoodDetails';

const SITE_URL = 'https://roseville.org';
const FORM_URL = 'https://readdy.ai/api/form/d6a3bbtt1k61917cmhh0';

// Approximate geo coords per neighborhood slug
const NEIGHBORHOOD_GEO: Record<string, { lat: number; lng: number }> = {
  'historic-old-town':      { lat: 38.7315, lng: -121.2882 },
  'westfield-galleria':     { lat: 38.7612, lng: -121.3101 },
  'creekside-town-center':  { lat: 38.7780, lng: -121.3450 },
  'douglas-boulevard':      { lat: 38.7490, lng: -121.3200 },
  'fountains-at-roseville': { lat: 38.7650, lng: -121.3050 },
  'sierra-vista-crossing':  { lat: 38.7200, lng: -121.3300 },
  'east-roseville':         { lat: 38.7450, lng: -121.2600 },
};

function buildNeighborhoodSchema(neighborhood: NonNullable<ReturnType<typeof getNeighborhoodBySlug>>) {
  const slug = neighborhood.slug;
  const pageUrl = `${SITE_URL}/neighborhoods/${slug}`;
  const geo = NEIGHBORHOOD_GEO[slug];

  // ── 1. Place (Neighborhood) ──────────────────────────────────────────────
  const placeSchema = {
    '@type': 'Place',
    '@id': `${pageUrl}#place`,
    name: `${neighborhood.name} — Roseville, CA`,
    description: neighborhood.longDescription,
    url: pageUrl,
    image: neighborhood.heroImage,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Roseville',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
    ...(geo ? { geo: { '@type': 'GeoCoordinates', latitude: geo.lat, longitude: geo.lng } } : {}),
    containedInPlace: {
      '@type': 'City',
      name: 'Roseville',
      '@id': 'https://en.wikipedia.org/wiki/Roseville,_California',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: neighborhood.avgRating.toFixed(1),
      reviewCount: neighborhood.totalReviews,
      bestRating: '5',
      worstRating: '1',
    },
    amenityFeature: neighborhood.highlights.map((h) => ({
      '@type': 'LocationFeatureSpecification',
      name: h,
      value: true,
    })),
  };

  // ── 2. ItemList — top businesses ─────────────────────────────────────────
  const featuredBusinesses = neighborhood.businesses.filter((b) => b.featured).slice(0, 6);
  const itemListSchema = {
    '@type': 'ItemList',
    '@id': `${pageUrl}#businesses`,
    name: `Top Businesses in ${neighborhood.name}, Roseville CA`,
    description: `Featured local businesses in the ${neighborhood.name} district of Roseville, California`,
    numberOfItems: featuredBusinesses.length,
    itemListElement: featuredBusinesses.map((biz, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'LocalBusiness',
        name: biz.name,
        description: biz.description,
        address: {
          '@type': 'PostalAddress',
          streetAddress: biz.address,
          addressLocality: 'Roseville',
          addressRegion: 'CA',
          addressCountry: 'US',
        },
        telephone: biz.phone,
        url: biz.website,
        priceRange: biz.priceRange,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: biz.rating.toFixed(1),
          reviewCount: biz.reviews,
          bestRating: '5',
          worstRating: '1',
        },
        openingHours: biz.hours,
      },
    })),
  };

  // ── 3. BreadcrumbList ────────────────────────────────────────────────────
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',          item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Neighborhoods', item: `${SITE_URL}/neighborhoods` },
      { '@type': 'ListItem', position: 3, name: neighborhood.name, item: pageUrl },
    ],
  };

  // ── 4. FAQPage — neighborhood-specific questions ─────────────────────────
  const faqSchema = {
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity: [
      {
        '@type': 'Question',
        name: `What is ${neighborhood.name} in Roseville, CA known for?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: neighborhood.description,
        },
      },
      {
        '@type': 'Question',
        name: `How many businesses are in ${neighborhood.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${neighborhood.name} has ${neighborhood.businessCount} businesses, including ${neighborhood.diningCount} dining options. The district is known for ${neighborhood.highlights.slice(0, 3).join(', ')}.`,
        },
      },
      {
        '@type': 'Question',
        name: `What is the Walk Score for ${neighborhood.name} in Roseville?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${neighborhood.name} has a Walk Score of ${neighborhood.walkScore} and a Transit Score of ${neighborhood.transitScore}, making it ${neighborhood.walkScore >= 70 ? 'very walkable' : 'somewhat walkable'} for daily errands.`,
        },
      },
      {
        '@type': 'Question',
        name: `What are the top things to do in ${neighborhood.name}, Roseville?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Popular activities and highlights in ${neighborhood.name} include: ${neighborhood.highlights.join(', ')}. ${neighborhood.nearbyAttractions.map((a) => `${a.name} is ${a.distance} away`).join('; ')}.`,
        },
      },
      {
        '@type': 'Question',
        name: `Is parking available in ${neighborhood.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: neighborhood.parkingInfo,
        },
      },
    ],
  };

  // ── Combine into @graph ──────────────────────────────────────────────────
  return {
    '@context': 'https://schema.org',
    '@graph': [placeSchema, itemListSchema, breadcrumbSchema, faqSchema],
  };
}

export default function NeighborhoodDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const neighborhood = getNeighborhoodBySlug(slug || '');

  // Guard — must be before useSEO to avoid hook-order issues, so we pass
  // safe fallback values when neighborhood is undefined and let the Navigate
  // handle the redirect after hooks run.
  const safeNeighborhood = neighborhood ?? {
    name: 'Neighborhood',
    slug: '',
    description: '',
    longDescription: '',
    accentColor: '#14B8A6',
    heroImage: '',
    businessCount: 0,
    walkScore: 0,
    transitScore: 0,
    diningCount: 0,
    avgRating: 0,
    totalReviews: 0,
    mapQuery: '',
    highlights: [],
    categories: [],
    businesses: [],
    nearbyAttractions: [],
    parkingInfo: '',
    transitInfo: '',
    tagline: '',
  };

  useSEO({
    title: `${safeNeighborhood.name} Neighborhood Guide — Roseville CA | Homes, Schools & Amenities`,
    description: `Explore ${safeNeighborhood.name} in Roseville, California. Discover homes for sale, schools, parks, restaurants, and ${safeNeighborhood.businessCount}+ local businesses. Complete neighborhood guide with walk scores, amenities, and community info.`,
    keywords: `${safeNeighborhood.name} Roseville CA, ${safeNeighborhood.name} neighborhood, homes for sale ${safeNeighborhood.name}, schools in ${safeNeighborhood.name}, Roseville neighborhoods, living in ${safeNeighborhood.name}, ${safeNeighborhood.highlights.slice(0, 3).join(', ')}`,
    canonical: `/neighborhoods/${safeNeighborhood.slug}`,
    ogType: 'website',
    ogImage: `https://public.readdy.ai/ai/img_res/a015eb7975ae9c4687517a85a36d62da.jpg$%7BencodeURIComponent%28safeNeighborhood.name%29%7D%20Roseville%20California%20with%20well-maintained%20homes%20tree-lined%20streets%20and%20community%20parks%20suburban%20living&width=1200&height=630&seq=roseville-${safeNeighborhood.slug}-og&orientation=landscape`,
    ogImageAlt: `${safeNeighborhood.name} neighborhood in Roseville, CA`,
    schema: neighborhood ? buildNeighborhoodSchema(neighborhood) : undefined,
  });

  if (!neighborhood) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <NeighborhoodHero neighborhood={neighborhood} />
      <StickyNav accentColor={neighborhood.accentColor} />
      <div id="overview">
        <OverviewStats neighborhood={neighborhood} />
      </div>
      <BusinessListings neighborhood={neighborhood} />
      <NeighborhoodMap neighborhood={neighborhood} />
      <ContactCTA neighborhood={neighborhood} formUrl={FORM_URL} />
      <MailchimpNewsletter />

      {/* Featured Real Estate Partners */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-home-heart-line"></i>
              <span>Looking to Move Here?</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Connect With Local Real Estate Experts
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Work with verified agents who specialize in {neighborhood.name} properties
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-star-line text-2xl text-emerald-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Expert Agents</div>
                  <div className="text-sm text-gray-600">Local market specialists</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Verified Partners</div>
                  <div className="text-sm text-gray-600">Licensed &amp; reviewed</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-hand-heart-line text-2xl text-cyan-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Free Service</div>
                  <div className="text-sm text-gray-600">No obligation consultations</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="/relocation-partners"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              <span>Find Your Agent</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
